/**
 * Catalogue of legal documents the portal hosts at `/legal/<slug>`.
 *
 * Each entry is a contract artifact: the slug is the URL + the
 * filename in `public/legal/`, the version + lastUpdated travel with
 * every render so customers + auditors can see exactly which
 * revision they're looking at, and `sha256` (computed at build time
 * eventually — for now a placeholder) is the hash the acceptance-
 * tracker stores alongside the `accepted_at` timestamp.
 *
 * Naming convention: `<kind>.md` under `public/legal/`. Same slug
 * powers the URL: `/legal/<slug>` → fetches `/legal/<slug>.md` →
 * rendered by `LegalDocumentView`. Adding a new doc is a 3-step
 * tweak (commit the markdown, add an entry here, add nothing else).
 *
 * Why MD source + portal render (not PDF):
 *   - Diff-able in git: every change is a reviewable PR.
 *   - Linkable: customer support can paste an exact URL to a clause.
 *   - Audit trail: `sha256` of the rendered content per version
 *     means we can prove what a customer accepted on `accepted_at`.
 *   - Adri (legal counsel) can read the source on Bitbucket with
 *     line numbers, or the rendered version in the portal. Both
 *     show the same content.
 *   - PDFs are generated on-demand for enterprise customers
 *     (md → pdf via a build-time script — deferred, see
 *     foundations/PENDING-WORK.md → "legalDocuments.ts:25").
 *
 * Versioning policy:
 *   - Material change (clauses added/removed, scope expansion,
 *     liability change) → bump major version (1.x → 2.0), customers
 *     re-prompted next login.
 *   - Cosmetic change (typo, formatting, clarification with no
 *     legal substance) → bump minor (2.0 → 2.1), no re-prompt.
 *   - Both update `lastUpdated`.
 */

export type LegalDocumentKind =
  | "privacy"
  | "terms-of-service"
  | "cookies"
  | "dpa"
  | "msa"
  | "subprocessors"
  | "master-agreement";

export type LegalDocumentMeta = {
  slug: LegalDocumentKind;
  title: string;
  description: string;
  /** Semantic version. Major bump = material change, re-prompt customers. */
  version: string;
  /** ISO date — when this version went live. */
  lastUpdated: string;
  /**
   * Scope of the document:
   *   - `user`  — accepted per individual person (Privacy, ToS).
   *   - `org`   — accepted by the Owner on behalf of the organisation
   *               (DPA, MSA).
   *   - `info`  — informational only, no acceptance required
   *               (Cookies, Subprocessors).
   */
  scope: "user" | "org" | "info";
  /**
   * Whether acceptance is collected at signup, paid activation, or
   * never. `null` for info-only docs.
   */
  acceptanceMoment: "signup" | "paid-activation" | null;
};

/**
 * Effective date for the 2026-05-27 revision wave — the V8 SOAP
 * HEALTH calibration pass that brought every doc in this catalogue
 * to a coherent baseline. All six docs (plus the new master-
 * agreement) carry this date today:
 *
 *   - master-agreement v1.0 (new — single click-through contract)
 *   - privacy v2.0 → v3.0 (material: 72h → 48h breach,
 *     pseudonymization, BAA path, dual-mode OAuth, SDK case,
 *     corp identifiers)
 *   - terms-of-service v2.0 → v3.0 (material: Delaware courts +
 *     AAA mediation supersedes binding arbitration; USD 25k
 *     liability floor; minor rejection clause; pseudonymization;
 *     dual-mode integration patterns)
 *   - cookies v2.0 → v2.1 (cosmetic: real cookie names)
 *   - subprocessors v1.0 → v1.1 (cosmetic: no Bitbucket leak,
 *     CPF-UIR-106 vocabulary)
 *   - dpa v1.0 → v2.0 (material: 72h → 48h breach, canonical
 *     provider names, integration patterns row, corp identifiers,
 *     sub-processor reference bumped to v1.1)
 *   - msa v1.0 → v2.0 (material: 99.9% → 97% rolling-90-day SLA,
 *     48h breach, Delaware courts + AAA mediation, Enterprise tier
 *     scope, support email typo fix)
 *
 * The prior wave constant `V3_INITIAL_RELEASE = "2026-05-25"` was
 * removed when this wave subsumed every doc that referenced it.
 * Future single-doc bumps that don't share a wave date with
 * anything else inline their `lastUpdated` directly — only promote
 * to a named constant when 2+ docs land on the same date.
 */
const V3_REVISION_2026_05_27 = "2026-05-27";

export const LEGAL_DOCUMENTS: readonly LegalDocumentMeta[] = [
  {
    slug: "privacy",
    title: "Privacy Policy",
    description:
      "How ROOK collects, processes and protects personal data — yours and your end users'.",
    // v3.0 — material revision calibrated against the V8 SOAP HEALTH
    // template (the one counsel has signed prior). Breach notification
    // tightened from 72h → 48h to match what we already commit on
    // paper. Pseudonymization explicitly distinguished from
    // anonymization under GDPR Art. 4(5). HIPAA scope clarified to
    // include the BAA-available path for Covered Entity Customers.
    // Dual-mode Provider OAuth (Rook-branded vs Branded Auth Process)
    // and SDK on-device extraction pattern made explicit. Corporate
    // identifiers (EIN, address, CEO) filled. ISO 27001 working-
    // towards added alongside SOC 2. Customers prompted to re-accept
    // at next signup per CPF-NFR-018-style acceptance moment.
    version: "3.0",
    lastUpdated: V3_REVISION_2026_05_27,
    scope: "user",
    acceptanceMoment: "signup",
  },
  {
    slug: "terms-of-service",
    title: "Terms of Service",
    description:
      "Your agreement to use the Customer Portal and ROOK Services (RookConnect + RookScore).",
    // v3.0 — material revision. Dispute resolution changed from
    // binding arbitration → Delaware courts + AAA 45-day mediation
    // (matches V8 SOAP commitment and master-agreement v1.0).
    // Production liability cap floor added (greater of 12-month
    // fees or USD 25,000) so low-spend Customers still have a
    // meaningful remedy. §5 acceptable use gained a minor-rejection
    // clause with 72h notification. §6.3 "de-identified" renamed
    // and rewritten to use pseudonymization (matches Privacy v3.0
    // + master-agreement v1.0). §8 added the three integration
    // patterns (Rook OAuth / Branded Auth Process / SDK on-device).
    // Corporate identifiers in the header. Re-prompt at signup.
    version: "3.0",
    lastUpdated: V3_REVISION_2026_05_27,
    scope: "user",
    acceptanceMoment: "signup",
  },
  {
    slug: "cookies",
    title: "Cookies Policy",
    description:
      "Cookies and tracking technologies used by ROOK and how to control them.",
    // v2.1 — cosmetic correction. Cookie table aligned with the
    // running v3 Portal: 3-cookie session split (`rook-portal-
    // session-{ta,ti,tr}`) instead of the `rook-session` /
    // `rook-refresh` / `rook-csrf` placeholders. Postal address
    // filled. No new tracking technologies, no scope change — minor
    // bump, no re-prompt.
    version: "2.1",
    lastUpdated: V3_REVISION_2026_05_27,
    scope: "info",
    acceptanceMoment: null,
  },
  {
    slug: "dpa",
    title: "Data Processing Agreement",
    description:
      "GDPR-aligned data processing terms between your organisation and ROOK. Required for EU customers, recommended for everyone processing personal data.",
    // v2.0 — material revision aligning with master-agreement v1.0,
    // Privacy v3.0, and Sub-processors v1.1. §9 breach notification
    // tightened from 72h → 48h. §18 contact filled (no more
    // placeholder address); EIN + CEO added; DPO statement made
    // specific. Annex 1 updated with canonical provider names + 300+
    // count + Integration patterns row referencing ToS §8.1.
    // Annex 2.D updated with structured-log redaction + 48h SLA.
    // Annex 3 sub-processor list bumped to v1.1. Re-prompt at signup
    // because the breach SLA change is a material commitment shift.
    version: "2.0",
    lastUpdated: V3_REVISION_2026_05_27,
    scope: "org",
    acceptanceMoment: "signup",
  },
  {
    slug: "msa",
    title: "Master Service Agreement",
    description:
      "The commercial contract governing Enterprise-tier paid use of ROOK Services. Supersedes the Master Agreement for negotiated enterprise customers. Includes SLA, fees, liability, indemnification, audit rights, and termination.",
    // v2.0 — material revision aligning Enterprise governance with
    // master-agreement v1.0 + DPA v2.0 + ToS v3.0. §6 + Annex A.2
    // SLA changed from 99.9% monthly → 97% rolling-90-day (matches
    // V8 SOAP commitment and what ROOKCONNECT's current architecture
    // can defensibly hit; 99.9% was wishful thinking). Credit tier
    // thresholds in A.3 recalibrated. §12 breach 72h → 48h. §20
    // dispute resolution: binding arbitration → Delaware courts +
    // AAA 45-day mediation. §9.3 De-identified → Aggregated and
    // pseudonymized (terminological precision). §1+§2 scope made
    // explicit as Enterprise tier (supersedes master-agreement).
    // Annex B standard-support email typo fixed (compliances@ →
    // support@). Re-prompt at paid-activation. **Open**: §17
    // insurance amounts (USD 2M cyber, USD 1M E&O, USD 1M CGL)
    // inherited from v1.0 need Adri sign-off that ROOK actually
    // carries those levels — they are commitments, not aspirations.
    version: "2.0",
    lastUpdated: V3_REVISION_2026_05_27,
    scope: "org",
    acceptanceMoment: "paid-activation",
  },
  {
    slug: "subprocessors",
    title: "Sub-processors",
    description:
      "Third-party service providers that process personal data on behalf of ROOK. Updated whenever the list changes.",
    // v1.1 — cosmetic correction. Replaced an internal Bitbucket
    // repository link in the Stripe "Status" row with a customer-
    // facing description of when Stripe will be added. Aligned
    // vocabulary to CPF-UIR-106 (users, not end users). No sub-
    // processors added or removed; no scope change — minor bump, no
    // re-prompt.
    version: "1.1",
    lastUpdated: V3_REVISION_2026_05_27,
    scope: "info",
    acceptanceMoment: null,
  },
  {
    slug: "master-agreement",
    title: "Master Agreement",
    description:
      "Single click-through contract covering the essentials of ToS + DPA + MSA. Sign once at signup to use ROOK self-service. Enterprise tier supersedes via a negotiated MSA + DPA.",
    // v1.0 — calibrated against the V8 SOAP HEALTH template Adri
    // (counsel) signed prior, so this doc inherits the commitments
    // ROOK has already made on paper (97% uptime over rolling 90-day
    // window; 48-hour breach notification; Delaware courts + AAA
    // 45-day mediation window — NOT arbitration; pseudonymization, not
    // anonymization — V8 conflated the two and that's legally wrong
    // under GDPR Art. 4(5)). Adri still needs to sign off on the
    // delta vs V8 (corp identifiers, dual-mode Provider OAuth, SDK
    // case made explicit, BAA stance placeholder, sub-processors
    // 30-day notice, liability cap retained as industry standard).
    // Bump to 2.0 only on a material change (clauses added/removed,
    // scope expansion, liability shift) — minor wording/typo fixes
    // bump to 1.1.
    version: "1.0",
    lastUpdated: V3_REVISION_2026_05_27,
    scope: "user",
    acceptanceMoment: "signup",
  },
];

/**
 * Docs a self-service signup must record acceptance for: **user-scoped
 * AND collected at signup** (today: Privacy, Terms of Service, Master
 * Agreement). Single source of truth for BOTH the signup clickwrap copy
 * and the acceptance POSTs, so the UI and the audit trail can never
 * drift.
 *
 * Org-scoped signup docs (e.g. the DPA) are deliberately NOT here — but
 * this is a PRODUCT choice, not a technical limit. The Master Agreement
 * (user-scope) already covers the DPA/MSA essentials for self-service,
 * so signup records that instead. Org-scope IS now recordable: the
 * cpf-contract-acceptances handler keys org acceptances by the session's
 * `client_id` (client ≡ org — no orgUuid needed). Flip this filter to
 * include client-scope if self-service should also record the standalone
 * DPA. The handler enforces scope server-side either way.
 */
export const SIGNUP_USER_ACCEPTANCE_DOCS: readonly LegalDocumentMeta[] =
  LEGAL_DOCUMENTS.filter(
    (d) => d.acceptanceMoment === "signup" && d.scope === "user",
  );

const LOOKUP = new Map(LEGAL_DOCUMENTS.map((d) => [d.slug, d]));

export const lookupLegalDocument = (
  slug: string,
): LegalDocumentMeta | null => LOOKUP.get(slug as LegalDocumentKind) ?? null;

/** Public URL for a doc, used when sharing links externally. */
export const legalDocumentUrl = (
  slug: LegalDocumentKind,
  baseUrl: string = "",
): string => `${baseUrl}/legal/${slug}`;

/**
 * Path to the raw markdown source — fetched at runtime by
 * LegalDocumentView. Vite serves `public/` from the root, so the
 * URL is the same path with `.md` appended.
 */
export const legalDocumentSourcePath = (slug: LegalDocumentKind): string =>
  `/legal/${slug}.md`;
